export type DownloadDocumentType = "receipt" | "invoice";

export const PDF_MIME_TYPE = "application/pdf";

interface FetchProtectedFileParams {
  accessToken: string;
  streamUrl: string;
}

const LINE_IN_APP_BROWSER_RE = /\bLine\//i;
const IOS_RE = /iPhone|iPad|iPod/i;
const ANDROID_RE = /Android/i;
// Android System WebView tags its user agent with "; wv)". Apps embedding it
// (LINE, Facebook, Instagram…) are handed blob: downloads they cannot fetch.
const ANDROID_WEBVIEW_RE = /;\s*wv\)/i;

export function isLineInAppBrowser() {
  return LINE_IN_APP_BROWSER_RE.test(window.navigator.userAgent);
}

// iPadOS 13+ reports a desktop Mac user agent; touch support gives it away.
function isIOS() {
  const { userAgent, maxTouchPoints } = window.navigator;
  return IOS_RE.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
}

/**
 * How this browser can put a file on the device:
 * - share:    the share sheet ("Save Image" / "Save to Files"). Every iOS browser
 *             is WebKit, where <a download> may navigate to the file instead.
 * - download: <a download> — Android browsers and desktop.
 * - external: LINE cannot save files, but can be asked to open the system browser.
 * - manual:   nothing works here; the user has to long-press or switch browsers.
 */
export type SaveMethod = "share" | "download" | "external" | "manual";

export function getSaveMethod(fileName: string, mimeType: string): SaveMethod {
  const { userAgent } = window.navigator;

  if (isIOS()) {
    const probe = new File([], fileName, { type: mimeType });
    if (typeof navigator.canShare === "function" && navigator.canShare({ files: [probe] })) {
      return "share";
    }
    return isLineInAppBrowser() ? "external" : "manual";
  }

  if (ANDROID_RE.test(userAgent)) {
    if (isLineInAppBrowser()) return "external";
    if (ANDROID_WEBVIEW_RE.test(userAgent)) return "manual";
  }

  return "download";
}

export type SaveOutcome = "saved" | "cancelled" | "retry" | "external" | "manual";

interface SaveFileOptions {
  fileName: string;
  mimeType: string;
  // Return an already-built File whenever possible: the share sheet only opens
  // while the tap that asked for it is still fresh.
  getFile: () => File | Promise<File>;
  // Reopens the flow in the system browser; only used inside LINE.
  openExternal?: () => void;
}

export async function saveFile({ fileName, mimeType, getFile, openExternal }: SaveFileOptions): Promise<SaveOutcome> {
  const method = getSaveMethod(fileName, mimeType);

  const fallBack = (): SaveOutcome => {
    if (openExternal && isLineInAppBrowser()) {
      openExternal();
      return "external";
    }
    return "manual";
  };

  if (method === "external" || method === "manual") {
    return fallBack();
  }

  const pending = getFile();
  const wasReady = pending instanceof File;
  const file = pending instanceof File ? pending : await pending;

  if (method === "download") {
    downloadFile(file);
    return "saved";
  }

  try {
    // Files only: adding text or a title drops "Save Image" from the iOS sheet.
    await navigator.share({ files: [file] });
    return "saved";
  } catch (error) {
    const name = (error as { name?: string })?.name;
    if (name === "AbortError") return "cancelled";
    // Fetching the file outlasted the tap. It is ready now, so the next tap works.
    if (name === "NotAllowedError" && !wasReady) return "retry";

    console.error("Share sheet failed:", error);
    return fallBack();
  }
}

function downloadFile(file: File) {
  const objectUrl = window.URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

export async function fetchProtectedFile({
  accessToken,
  streamUrl,
}: FetchProtectedFileParams) {
  const response = await fetch(streamUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  return response.blob();
}

export function toPdfFileName(name: string | null | undefined, fallback: string) {
  const baseName = name && name !== "undefined" ? name : fallback;
  return /\.pdf$/i.test(baseName) ? baseName : `${baseName}.pdf`;
}

export async function fetchPdfFile(streamUrl: string, fileName: string) {
  const accessToken = localStorage.getItem("token");
  if (!accessToken) {
    throw new Error("Missing access token");
  }

  const blob = await fetchProtectedFile({ accessToken, streamUrl });
  return new File([blob], fileName, { type: PDF_MIME_TYPE });
}

export const DOCUMENT_SAVE_MESSAGES: Record<SaveOutcome, string> = {
  saved: "บันทึกไฟล์แล้ว หากไม่พบ ให้ดูในแอปไฟล์หรือโฟลเดอร์ Downloads",
  cancelled: "",
  retry: "ไฟล์พร้อมแล้ว กรุณากดปุ่มดาวน์โหลดอีกครั้ง",
  external: "กำลังเปิดเอกสารในเบราว์เซอร์...",
  manual: "เบราว์เซอร์นี้บันทึกไฟล์ไม่ได้ กรุณาเปิดเว็บไซต์ใน Safari หรือ Chrome แล้วลองอีกครั้ง",
};

// Pages the LINE handoff opens. They load from "/" so they work even where the
// server does not fall back to index.html for deep links.
export const HANDOFF_VIEW_PARAM = "_view";
export type HandoffView = "qr" | "document";

const TOKEN_PARAM = "_t";
const LINE_EXTERNAL_BROWSER_PARAM = "openExternalBrowser";

// LINE catches this parameter on navigation and opens the URL in the system
// browser. Navigate once only: a second attempt can open the page twice.
function navigateToExternalBrowser(url: URL) {
  url.searchParams.set(LINE_EXTERNAL_BROWSER_PARAM, "1");
  window.location.href = url.toString();
}

export function openInExternalBrowser(view: HandoffView, params: Record<string, string | undefined>) {
  const url = new URL("/", window.location.origin);
  url.searchParams.set(HANDOFF_VIEW_PARAM, view);
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });

  const token = localStorage.getItem("token");
  if (token) url.searchParams.set(TOKEN_PARAM, token);

  navigateToExternalBrowser(url);
}

export function reopenInExternalBrowser() {
  const url = new URL(window.location.href);
  const token = localStorage.getItem("token");
  if (token) url.searchParams.set(TOKEN_PARAM, token);

  navigateToExternalBrowser(url);
}

// The system browser has its own storage, so the token travels in the URL.
// Store it, then drop it from the address bar and history — except inside LINE,
// where "open in browser" from LINE's own menu still has to carry it.
export function consumeHandoffToken() {
  const url = new URL(window.location.href);
  const token = url.searchParams.get(TOKEN_PARAM);
  if (!token) return;

  localStorage.setItem("token", token);
  if (isLineInAppBrowser()) return;

  url.searchParams.delete(TOKEN_PARAM);
  url.searchParams.delete(LINE_EXTERNAL_BROWSER_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}
